import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingBag,
  Category,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AttachMoney,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Link as LinkIcon,
  Clear as ClearIcon,
  Image as ImageIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import shopService, { Product, ProductStats, Store } from '../../services/shop.service';
import api from '../../services/api';

const PagaliShop: React.FC = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [digitalFile, setDigitalFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageMode, setImageMode] = useState<'upload' | 'url'>('upload');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [taxas, setTaxas] = useState<{ id: string; nome: string; percentagem: number; aplicaA: string }[]>([]);
  const [taxasDialog, setTaxasDialog] = useState(false);
  const [taxaForm, setTaxaForm] = useState({ id: '', nome: '', percentagem: 0, aplicaA: 'PRODUTO' });
  const [taxaEditId, setTaxaEditId] = useState<string | null>(null);
  const [savingTaxa, setSavingTaxa] = useState(false);
  const [formData, setFormData] = useState<Partial<Product> & { categoryId?: string }>({
    name: '',
    description: '',
    category: '',
    categoryId: '',
    price: 0,
    stock: 0,
    status: 'active',
    provider: '',
  });
  const [stats, setStats] = useState<ProductStats>({
    total: 0,
    active: 0,
    categories: 0,
    totalValue: 0,
  });

  const formatCurrency = (value: number) => {
    return `${Math.round((value / 100) || 0).toLocaleString('pt-PT')}$`;
  };

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label={t('common.active')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />
    );
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
      Recarga: 'primary',
      Voucher: 'secondary',
      Servico: 'success',
      Produto: 'warning',
      Digital: 'info',
    };
    return colors[category] || 'default';
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await shopService.getProducts({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        search: searchTerm || undefined,
      });

      if (response.success) {
        setProducts(response.data || []);
        setTotal(response.pagination?.total || 0);
        if (response.needsSetup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching products:', err);
      setError(t('shop.errorLoadingProducts'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, categoryFilter, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await shopService.getProductStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await shopService.getCategories();
      if (response.success && response.data.length > 0) {
        const cats = response.data.map((c: any) => ({ id: String(c.id), name: c.name }));
        setCategories(cats);
        setFormData(prev => ({
          ...prev,
          categoryId: prev.categoryId || cats[0]?.id || '',
          category: prev.category || cats[0]?.name || '',
        }));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const response = await shopService.getStores({ limit: 100 });
      if (response.success) {
        setStores(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  }, []);

  const fetchTaxas = useCallback(async () => {
    try {
      const res = await api.get('/admin/shop/taxas');
      if (res.data?.success) setTaxas(res.data.data || []);
    } catch { /* tabela ainda não existe — ignorar */ }
  }, []);

  const handleSaveTaxa = async () => {
    if (!taxaForm.nome) return;
    setSavingTaxa(true);
    try {
      if (taxaEditId) {
        await api.put(`/admin/shop/taxas/${taxaEditId}`, taxaForm);
      } else {
        await api.post('/admin/shop/taxas', taxaForm);
      }
      setTaxaForm({ id: '', nome: '', percentagem: 0, aplicaA: 'PRODUTO' });
      setTaxaEditId(null);
      await fetchTaxas();
      setSuccessMessage(taxaEditId ? 'Taxa actualizada' : 'Taxa criada');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao guardar taxa');
    } finally {
      setSavingTaxa(false);
    }
  };

  const handleDeleteTaxa = async (id: string) => {
    if (!window.confirm('Eliminar esta taxa?')) return;
    try {
      await api.delete(`/admin/shop/taxas/${id}`);
      await fetchTaxas();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao eliminar taxa');
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchStats();
    fetchCategories();
    fetchStores();
    fetchTaxas();
  }, [fetchProducts, fetchStats, fetchCategories]);

  // Fill category when categories load and dialog is open with no category selected
  useEffect(() => {
    if (editDialog && categories.length > 0 && !formData.categoryId) {
      setFormData(prev => ({
        ...prev,
        categoryId: categories[0].id,
        category: categories[0].name,
      }));
    }
  }, [categories, editDialog]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (product?: Product) => {
    setImageFile(null);
    setImageUrl('');
    setImagePreview(null);
    setImageMode('upload');
    if (product) {
      const matchedCat = categories.find(c => c.name === product.category);
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        category: product.category || categories[0]?.name || '',
        categoryId: matchedCat?.id || categories[0]?.id || '',
        price: Math.round((product.price || 0) / 100),
        stock: product.stock,
        status: product.status,
        provider: product.provider,
        storeId: product.storeId || '',
      });
      if (product.image) {
        if (product.image.startsWith('http://') || product.image.startsWith('https://')) {
          setImageMode('url');
          setImageUrl(product.image);
          setImagePreview(product.image);
        } else {
          setImageMode('upload');
          setImagePreview(product.image.startsWith('data:') ? product.image : `data:image/jpeg;base64,${product.image}`);
        }
      }
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        description: '',
        category: categories[0]?.name || '',
        categoryId: categories[0]?.id || '',
        price: 0,
        stock: 0,
        status: 'active',
        provider: '',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedProduct(null);
    setDigitalFile(null);
    setImageFile(null);
    setImageUrl('');
    setImagePreview(null);
    setImageMode('upload');
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const imageToSend = imageMode === 'url' ? (imageUrl || undefined) : (imagePreview || undefined);
      const dataToSend = { ...formData, price: (formData.price || 0) * 100, categoryId: formData.categoryId, image: imageToSend };
      let savedProductId: string | null = null;
      if (selectedProduct) {
        await shopService.updateProduct(selectedProduct.id, dataToSend);
        savedProductId = selectedProduct.id;
        setSuccessMessage(t('shop.productUpdated'));
      } else {
        const res: any = await shopService.createProduct(dataToSend as any);
        savedProductId = res?.data?.id || res?.id || null;
        setSuccessMessage(t('shop.productCreated'));
      }

      // Upload ficheiro digital se seleccionado
      if (digitalFile && savedProductId) {
        setUploadProgress(true);
        try {
          const fd = new FormData();
          fd.append('file', digitalFile);
          const upRes = await api.post(`/portal/digital/products/${savedProductId}/upload`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const { ficheiroUrl, formato, tamanhoMb, nomeFicheiro, storagePath } = upRes.data.data;
          await api.post(`/portal/digital/products/${savedProductId}/attach`, {
            ficheiroUrl, storagePath, nomeFicheiro, formato, tamanhoMb,
          });
          setSuccessMessage('Produto guardado e ficheiro digital associado com sucesso');
        } catch (uploadErr: any) {
          setError(`Produto guardado mas erro no upload: ${uploadErr.message}`);
        } finally {
          setUploadProgress(false);
        }
      }

      handleCloseEdit();
      fetchProducts();
      fetchStats();
    } catch (err: any) {
      console.error('Error saving product:', err);
      setError(t('shop.errorSavingProduct'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product: Product) => {
    if (window.confirm(t('shop.confirmDeleteProduct', { name: product.name }))) {
      try {
        await shopService.deleteProduct(product.id);
        setSuccessMessage(t('shop.productDeleted'));
        fetchProducts();
        fetchStats();
      } catch (err: any) {
        console.error('Error deleting product:', err);
        setError(t('shop.errorDeletingProduct'));
      }
    }
  };

  const handleSetupTables = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await shopService.setupTables();
      if (response.success) {
        setSuccessMessage(response.message);
        setNeedsSetup(false);
        fetchProducts();
        fetchStats();
        fetchCategories();
      }
    } catch (err: any) {
      console.error('Error setting up tables:', err);
      setError(t('shop.errorSetup'));
    } finally {
      setSettingUp(false);
    }
  };

  const handleRefresh = () => {
    fetchProducts();
    fetchStats();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('shop.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Gerir taxas (IVA, Envio, Transporte)">
            <Button variant="outlined" startIcon={<PercentIcon />} onClick={() => setTaxasDialog(true)}>
              Taxas
            </Button>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('shop.newProduct')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Setup Alert */}
      {!loading && needsSetup && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTables}
              disabled={settingUp}
            >
              {settingUp ? t('common.loading') : t('shop.setupTables')}
            </Button>
          }
        >
          {t('shop.setupNeeded')}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingBag color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.totalProducts')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.active}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.active')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Category color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.categories}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.category')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="warning" />
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(stats.totalValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.stockValue')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('shop.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchProducts()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.category')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.allFemale')}</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="active">{t('common.active')}</MenuItem>
                <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('shop.product')}</TableCell>
                    <TableCell>{t('shop.provider')}</TableCell>
                    <TableCell>{t('common.category')}</TableCell>
                    <TableCell align="right">{t('shop.price')}</TableCell>
                    <TableCell align="center">{t('shop.stock')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">{t('shop.noProducts')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.description}
                          </Typography>
                        </TableCell>
                        <TableCell>{product.provider}</TableCell>
                        <TableCell>
                          <Chip size="small" label={product.category} color={getCategoryColor(product.category)} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(product.price)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={product.stock}
                            variant="outlined"
                            color={product.stock > 100 ? 'success' : product.stock > 50 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell>{getStatusChip(product.status)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenEdit(product)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(product)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
            />
          </>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingBag />
          {selectedProduct ? t('shop.editProduct') : t('shop.newProduct')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('shop.productName')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.category')}
                value={formData.categoryId || ''}
                onChange={(e) => {
                  const cat = categories.find(c => c.id === e.target.value);
                  setFormData({ ...formData, categoryId: e.target.value, category: cat?.name || '' });
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('common.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('shop.store')}
                value={formData.storeId || ''}
                onChange={(e) => setFormData({ ...formData, storeId: e.target.value })}
              >
                <MenuItem value="">{t('shop.selectStore')}</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <MenuItem value="active">{t('common.active')}</MenuItem>
                <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={`${t('shop.price') || 'Preço'} (ECV$)`}
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Stock"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Taxa (Imposto / IVA)"
                value={formData.iva ?? 0}
                onChange={(e) => setFormData({ ...formData, iva: parseFloat(e.target.value) })}
                helperText="Selecione a taxa configurada ou introduza manualmente"
              >
                <MenuItem value={0}>Isento (0%)</MenuItem>
                {taxas.filter(t => t.aplicaA === 'PRODUTO').map(t => (
                  <MenuItem key={t.id} value={t.percentagem}>
                    {t.nome} ({t.percentagem}%)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {(formData.iva ?? 0) > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  IVA de {formData.iva}% será adicionado ao preço base na finalização da compra.
                  Preço final ao cliente: {Math.round((formData.price || 0) * (1 + (formData.iva || 0) / 100)).toLocaleString('pt-CV')}$00
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ImageIcon fontSize="small" />
                  Imagem do produto
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <Button
                    size="small"
                    variant={imageMode === 'upload' ? 'contained' : 'outlined'}
                    onClick={() => { setImageMode('upload'); setImageUrl(''); }}
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload
                  </Button>
                  <Button
                    size="small"
                    variant={imageMode === 'url' ? 'contained' : 'outlined'}
                    onClick={() => { setImageMode('url'); setImageFile(null); setImagePreview(null); }}
                    startIcon={<LinkIcon />}
                  >
                    Link URL
                  </Button>
                </Box>
                <input
                  type="file"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleImageFileChange}
                />
                {imageMode === 'upload' ? (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={() => imageInputRef.current?.click()}
                    sx={{ borderStyle: 'dashed', color: imageFile ? 'success.main' : 'text.secondary', borderColor: imageFile ? 'success.main' : 'divider' }}
                  >
                    {imageFile ? imageFile.name : 'Selecionar imagem (JPG, PNG, WEBP)'}
                  </Button>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="URL da imagem"
                    placeholder="https://exemplo.com/foto.jpg"
                    value={imageUrl}
                    onChange={(e) => { setImageUrl(e.target.value); setImagePreview(e.target.value || null); }}
                  />
                )}
                {imagePreview && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <img
                      src={imagePreview}
                      alt="preview"
                      style={{ maxHeight: 100, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }}
                      onError={() => setImagePreview(null)}
                    />
                    <Button
                      size="small"
                      color="error"
                      startIcon={<ClearIcon />}
                      onClick={() => { setImagePreview(null); setImageFile(null); setImageUrl(''); if (imageInputRef.current) imageInputRef.current.value = ''; }}
                    >
                      Remover
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.zip,.mp4,.mov,.epub"
                onChange={(e) => setDigitalFile(e.target.files?.[0] || null)}
              />
              <Button
                variant="outlined"
                fullWidth
                startIcon={digitalFile ? <AttachFileIcon /> : <CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ py: 1.5, borderStyle: 'dashed', borderColor: digitalFile ? 'success.main' : 'divider', color: digitalFile ? 'success.main' : 'text.secondary' }}
              >
                {digitalFile
                  ? `${digitalFile.name} (${(digitalFile.size / 1_048_576).toFixed(1)} MB)`
                  : 'Ficheiro digital (PDF, ZIP, MP4, EPUB) — opcional'}
              </Button>
              {digitalFile && (
                <Button size="small" color="error" onClick={() => setDigitalFile(null)} sx={{ mt: 0.5 }}>
                  Remover ficheiro
                </Button>
              )}
            </Grid>
          </Grid>
          {uploadProgress && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || !formData.price || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? t('common.saving') : selectedProduct ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Taxas Dialog ─────────────────────────────────────── */}
      <Dialog open={taxasDialog} onClose={() => { setTaxasDialog(false); setTaxaEditId(null); setTaxaForm({ id: '', nome: '', percentagem: 0, aplicaA: 'PRODUTO' }); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PercentIcon />
          Gestão de Taxas
        </DialogTitle>
        <DialogContent dividers>
          {/* Form add/edit */}
          <Typography variant="subtitle2" gutterBottom>
            {taxaEditId ? 'Editar taxa' : 'Nova taxa'}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth size="small"
                label="Nome da taxa"
                placeholder="ex: IVA Standard"
                value={taxaForm.nome}
                onChange={(e) => setTaxaForm({ ...taxaForm, nome: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number"
                label="Percentagem (%)"
                value={taxaForm.percentagem}
                onChange={(e) => setTaxaForm({ ...taxaForm, percentagem: parseFloat(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 0, max: 100, step: 0.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField
                fullWidth size="small" select
                label="Aplica a"
                value={taxaForm.aplicaA}
                onChange={(e) => setTaxaForm({ ...taxaForm, aplicaA: e.target.value })}
              >
                <MenuItem value="PRODUTO">Produto</MenuItem>
                <MenuItem value="ENVIO">Envio</MenuItem>
                <MenuItem value="TRANSPORTE">Transporte</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained" size="small"
                  onClick={handleSaveTaxa}
                  disabled={!taxaForm.nome || savingTaxa}
                  startIcon={savingTaxa ? <CircularProgress size={14} /> : <AddIcon />}
                >
                  {taxaEditId ? 'Actualizar' : 'Adicionar taxa'}
                </Button>
                {taxaEditId && (
                  <Button size="small" onClick={() => { setTaxaEditId(null); setTaxaForm({ id: '', nome: '', percentagem: 0, aplicaA: 'PRODUTO' }); }}>
                    Cancelar
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* List */}
          <Typography variant="subtitle2" gutterBottom>Taxas configuradas</Typography>
          {taxas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Ainda não há taxas configuradas.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell align="center">%</TableCell>
                  <TableCell align="center">Aplica a</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taxas.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.nome}</TableCell>
                    <TableCell align="center">
                      <Chip label={`${t.percentagem}%`} size="small" color={t.percentagem > 0 ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={t.aplicaA === 'PRODUTO' ? 'Produto' : t.aplicaA === 'ENVIO' ? 'Envio' : 'Transporte'}
                        size="small"
                        color={t.aplicaA === 'PRODUTO' ? 'primary' : t.aplicaA === 'ENVIO' ? 'info' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => { setTaxaEditId(t.id); setTaxaForm({ id: t.id, nome: t.nome, percentagem: t.percentagem, aplicaA: t.aplicaA }); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteTaxa(t.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTaxasDialog(false); setTaxaEditId(null); setTaxaForm({ id: '', nome: '', percentagem: 0, aplicaA: 'PRODUTO' }); }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PagaliShop;
